import { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  name: string;
  type: 'anchor' | 'link';
  target: string; // id for anchors, path for links
}

const navItems: NavItem[] = [
  { name: 'work', type: 'anchor', target: 'work' },
  { name: 'edutainment', type: 'link', target: '/edutainment' },
  { name: 'contact', type: 'anchor', target: 'contact' },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = useCallback((id: string) => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.pathname, navigate]);

  const handleNavClick = useCallback((item: NavItem) => {
    setMenuOpen(false);
    if (item.type === 'anchor') {
      scrollToSection(item.target);
    }
  }, [scrollToSection]);

  const renderNavItem = (item: NavItem, className: string) => {
    if (item.type === 'anchor') {
      return (
        <button
          key={item.name}
          onClick={() => handleNavClick(item)}
          className={className}
        >
          {item.name}
        </button>
      );
    }
    return (
      <Link
        key={item.name}
        to={item.target}
        className={className}
        onClick={() => setMenuOpen(false)}
      >
        {item.name}
      </Link>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-ink/95 backdrop-blur-sm">
      <nav className="flex items-center justify-between px-6 md:px-12 lg:px-16 py-4">
        <Link
          to="/"
          className="font-sans text-sm tracking-widest uppercase text-vellum hover:text-accent transition-colors"
        >
          neumann's workshop
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6 font-sans text-sm tracking-wide">
          {navItems.map((item) =>
            renderNavItem(item, 'text-vellum-muted hover:text-accent transition-colors lowercase')
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-vellum-muted hover:text-vellum transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6h16.5" />
            )}
          </svg>
        </button>
      </nav>

      {/* Hairline rule */}
      <div className="h-px bg-rule" />

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-ink border-t border-rule px-6 py-4 space-y-3 font-sans text-sm">
          {navItems.map((item) =>
            renderNavItem(item, 'block text-vellum-muted hover:text-accent transition-colors lowercase')
          )}
        </div>
      )}
    </header>
  );
}
